<script lang="ts">
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { tailwindSurfaceFontClass } from '@@/bits/font'
import { __, p__ } from '@@/bits/intl'
import type {
  CustomTableColumn,
  NonPostAttributeTableColumn,
  StandardTableColumn,
  TableColumn,
} from '@@/bits/table_layout'
import { WallWishGroupByTypes } from '@@/enums'
import OzBaseButton from '@@/library/v4/components/OzBaseButton.vue'
import { openNewCustomPropertyPanel } from '@@/native_bridge/actions'
import postMessage from '@@/native_bridge/post_message'
import { useSurfaceStore } from '@@/pinia/surface'
import { useCommentsStore } from '@@/pinia/surface_comments'
import { useSurfaceOnboardingDemoPadletPanelStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_store'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { PostPropertiesSubpanel, useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useReactionsStore } from '@@/pinia/surface_reactions'
import { useSurfaceSettingsStore } from '@@/pinia/surface_settings'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import type { Id, LabelledSection, WallCustomPostProperty, WallGroupPost } from '@@/types'
import SurfaceAddSectionButton from '@@/vuecomponents/SurfaceAddSectionButton.vue'
import SurfacePostsTableColumnHeaders from '@@/vuecomponents/SurfacePostsTableColumnHeaders.vue'
import SurfacePostsTableRows from '@@/vuecomponents/SurfacePostsTableRows.vue'
import SurfacePostsTableSectionHeader from '@@/vuecomponents/SurfacePostsTableSectionHeader.vue'
import { useScrollToNewestPost } from '@@/vuecomposables/surface_scroll_to_newest_post'
import { storeToRefs } from 'pinia'
import type { Ref } from 'vue'
import { computed, ref } from 'vue'

export const DEFAULT_COLUMN_WIDTH = 200
const EXPAND_COLUMN_THRESHOLD = 256 // if cell content exceeds this length, we expand the column width

export default {}
</script>

<script setup lang="ts">
withDefaults(
  defineProps<{
    reactionIdsByPostId: Record<Id, Id[]>
    commentIdsByPostId: Record<Id, Id[]>
  }>(),
  {
    reactionIdsByPostId: () => ({}),
    commentIdsByPostId: () => ({}),
  },
)

const surfacePostsStore = useSurfacePostsStore()
const { allSortedPostsByGroup, allSortedPosts } = storeToRefs(surfacePostsStore)
const { shouldDisplayGroup } = surfacePostsStore
const surfaceStore = useSurfaceStore()
const { backgroundLuminance, isReadOnly, isFrozen, colorScheme, fontId } = storeToRefs(surfaceStore)
const surfaceSettingsStore = useSurfaceSettingsStore()
const { sortedGroups, wishGroupBy, isGroupByNone, isGroupBySection, isGroupBySingleSelect } = storeToRefs(
  useSurfaceWishArrangementStore(),
)
const { canIModerate, canUpdateWallSettings } = storeToRefs(useSurfacePermissionsStore())
const { isDemoPadletPanelDesktop } = storeToRefs(useSurfaceOnboardingDemoPadletPanelStore())
const surfacePostPropertiesStore = useSurfacePostPropertiesStore()
const { wallCustomPostProperties } = storeToRefs(surfacePostPropertiesStore)
const reactionsStore = useReactionsStore()
const { hasReactions } = storeToRefs(reactionsStore)
const commentsStore = useCommentsStore()

const rootElement: Ref<HTMLDivElement | null> = ref(null)

useScrollToNewestPost(rootElement)

const isSectioned = computed(() => wishGroupBy.value !== WallWishGroupByTypes.None)

const sectionsToDisplay = computed<Required<LabelledSection>[]>(() => {
  return sortedGroups.value.map((group: WallGroupPost) => {
    return {
      ...group,
      ariaLabelledby: `section-${group.id}`,
    } as Required<LabelledSection>
  })
})

const isSectionEmpty = (sectionId: string | number): boolean => {
  const postsInGroup = allSortedPostsByGroup.value[sectionId]
  return !postsInGroup?.length
}

// #region table columns
const showReactionsColumn = computed(() => {
  return hasReactions.value || surfaceSettingsStore.isReactable
})

const showCommentsColumn = computed(() => {
  return commentsStore.hasComments || surfaceSettingsStore.isCommentable
})

const rowNumberColumn: NonPostAttributeTableColumn = {
  field: 'row_number',
  label: '',
  show: true,
  width: 36,
}

const maxPostBodyLength = computed(() => {
  return Math.max(...allSortedPosts.value.map((post) => post.body?.length ?? 0))
})

const standardColumns = computed<StandardTableColumn[]>(() => [
  { field: 'author', label: __('Author'), show: surfaceSettingsStore.showAuthor, width: DEFAULT_COLUMN_WIDTH },
  {
    field: 'subject',
    label: __('Subject'),
    show: surfacePostsStore.hasPostsWithSubject || surfacePostPropertiesStore.isSubjectEnabled,
    width: DEFAULT_COLUMN_WIDTH,
  },
  {
    field: 'attachment',
    label: __('Attachment'),
    show: surfacePostsStore.hasPostsWithAttachments || surfacePostPropertiesStore.attachmentsEnabled,
    width: DEFAULT_COLUMN_WIDTH,
  },
  {
    field: 'body',
    label: __('Body'),
    show: surfacePostsStore.hasPostsWithBody || surfacePostPropertiesStore.isBodyEnabled,
    width: maxPostBodyLength.value > EXPAND_COLUMN_THRESHOLD ? DEFAULT_COLUMN_WIDTH * 2 : DEFAULT_COLUMN_WIDTH,
  },
  {
    field: 'location',
    label: __('Location'),
    show: surfacePostsStore.hasPostsWithLocation || surfacePostPropertiesStore.isLocationEnabled,
    width: DEFAULT_COLUMN_WIDTH,
  },
])

const getWidthForCustomProperty = (prop: WallCustomPostProperty) => {
  if (prop.data_type !== 'text') return DEFAULT_COLUMN_WIDTH

  // Assumption: generally only text properties can have very long values,
  // so for them we expand column width if necessary.
  const allTextPropertyValues: number[] = allSortedPosts.value
    .filter((post) => {
      if (!prop.id) return false
      return post.custom_properties?.[prop.id] != null
    })
    .map((post) => {
      if (!prop.id) return 0
      return (post.custom_properties?.[prop.id] as string).length
    })

  return Math.max(...allTextPropertyValues) > EXPAND_COLUMN_THRESHOLD ? DEFAULT_COLUMN_WIDTH * 2 : DEFAULT_COLUMN_WIDTH
}

const customPropertyColumns = computed<CustomTableColumn[]>(() =>
  wallCustomPostProperties.value
    .filter((prop) => prop.data_type !== 'link_button')
    .map((prop) => {
      return {
        field: `custom_${prop.id}`,
        label: prop.name,
        show: true,
        width: getWidthForCustomProperty(prop),
        customProperty: prop,
      }
    }),
)

const linkButtonColumn = computed<CustomTableColumn[]>(() =>
  wallCustomPostProperties.value
    .filter((prop) => prop.data_type === 'link_button')
    .map((prop) => ({
      field: `custom_${prop.id}`,
      label: prop.name,
      show: true,
      width: DEFAULT_COLUMN_WIDTH,
      customProperty: prop,
    })),
)

const socialColumns = computed<NonPostAttributeTableColumn[]>(() => [
  { field: 'spacer', label: '', show: showReactionsColumn.value || showCommentsColumn.value, width: 16 },
  { field: 'reactions', label: __('Reactions'), show: showReactionsColumn.value, width: 160 },
  { field: 'comments', label: __('Comments'), show: showCommentsColumn.value, width: 160 },
])

// Full list of columns for the padlet.
const tableColumns = computed<TableColumn[]>(() => [
  rowNumberColumn,
  ...standardColumns.value,
  ...customPropertyColumns.value,
  ...linkButtonColumn.value,
  ...socialColumns.value,
])

const allColumnsToShow = computed<TableColumn[]>(() => {
  return tableColumns.value.filter((column) => column.show)
})

// #endregion

const tableWidth = computed(() => {
  return allColumnsToShow.value.reduce((total, column) => total + column.width, 0)
})

const postBannerWidth = computed(() => {
  return [...standardColumns.value, ...customPropertyColumns.value, ...linkButtonColumn.value]
    .filter((col) => col.show)
    .reduce((total, column) => total + column.width, 0)
})

const fontClass = computed((): string => {
  return tailwindSurfaceFontClass(fontId.value)
})

// #region drag and drop
const isDraggingSection = ref(false)
const onDragStartSection = () => {
  isDraggingSection.value = true
}

const onDragEndSection = () => {
  isDraggingSection.value = false
}
// #endregion

const openAddCustomPropertyPanel = () => {
  if (device.app) {
    postMessage(openNewCustomPropertyPanel())
  } else {
    surfacePostPropertiesStore.showSettingsPanelDirectly({
      postPropertiesSubpanel: PostPropertiesSubpanel.CustomPostProperties,
    })
  }
}
</script>

<template>
  <div
    :id="'wish-list' /* used by banksy to obtain appropriate width of screenshots */"
    ref="rootElement"
    class="surface-posts-table"
    :style="{ width: `${tableWidth}px` }"
  >
    <section
      v-for="group in sectionsToDisplay"
      v-show="shouldDisplayGroup(group)"
      :id="`section-${group.id}`"
      :key="group.id"
      :data-section-id="group.id"
      :data-rank="group.sort_index"
      :data-testid="`surfacePostsTableSection-${group.id}`"
    >
      <!-- Section Header -->
      <SurfacePostsTableSectionHeader
        v-if="isAppUsing('groupPostsV2') ? !isGroupByNone : isSectioned"
        :section="group"
        class="mb-1 mt-3"
        :hide-buttons="isDraggingSection"
        :background-luminance="backgroundLuminance"
        @drag-start="onDragStartSection"
        @drag-end="onDragEndSection"
      />

      <div v-if="!isSectionEmpty(group.id)" v-show="!isDraggingSection" :class="['flex ms-3', fontClass]">
        <div :class="['posts-table relative']">
          <SurfacePostsTableColumnHeaders
            :table-columns-to-show="allColumnsToShow"
            :background-luminance="backgroundLuminance"
            :class="['sticky top-0 z-[2]']"
          />

          <SurfacePostsTableRows
            :id="`group-posts-${group.id}`"
            :posts="allSortedPostsByGroup[group.id] || []"
            :all-columns-to-show="allColumnsToShow"
            :background-luminance="backgroundLuminance"
            :color-scheme="colorScheme"
            :comment-ids-by-post-id="commentIdsByPostId"
            :reaction-ids-by-post-id="reactionIdsByPostId"
            :post-banner-width="postBannerWidth"
            :table-width="tableWidth"
          />
        </div>
        <!-- "Add field" button -->
        <div v-if="canUpdateWallSettings" :class="['px-4 pt-3']">
          <OzBaseButton
            :class="[
              'h-9 px-3 py-2',
              'rounded-xl',
              'shadow-[0px_0.5px_1px_0px_rgba(0,0,0,0.20)]',
              'sticky top-0',
              'focus-visible:ring-2',
              // Background and text colors
              backgroundLuminance === 'light'
                ? 'bg-white-100/[0.32] text-dark-text-200'
                : 'bg-black/[0.24] text-light-text-200',
              // Focus ring colors
              backgroundLuminance === 'light' ? 'focus-visible:ring-grape-500' : 'focus-visible:ring-canary-500',
              // Hover/active text colors
              backgroundLuminance === 'light' ? 'hover:active:text-dark-text-400' : 'hover:active:text-light-text-400',
            ]"
            data-testid="surfacePostsTableAddFieldButton"
            @click="openAddCustomPropertyPanel"
          >
            <span class="text-body-small font-semibold whitespace-nowrap">
              {{ p__('Add a new custom post field', 'Add field') }}
            </span>
          </OzBaseButton>
        </div>
      </div>
    </section>

    <div
      v-if="
        (isGroupBySection || isGroupBySingleSelect) &&
        canIModerate &&
        !isReadOnly &&
        !isFrozen &&
        !isDemoPadletPanelDesktop
      "
      :class="['pt-2 ps-12 pb-16', 'relative']"
    >
      <SurfaceAddSectionButton :class="['sticky start-0']" />
    </div>
  </div>
</template>
